/**
 * Cognito configuration & helpers.
 *
 * Runtime is lazy-loaded so the app can still render in local UI-only mode
 * and won't hard-crash on startup if the Cognito browser package fails.
 */

import type { CognitoUserSession } from "amazon-cognito-identity-js";

const POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "";
const CLIENT_ID = import.meta.env.VITE_COGNITO_APP_CLIENT_ID ?? "";
const UI_ONLY_MODE =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true";
const DEV_MODE =
  String(import.meta.env.VITE_DEV_MODE ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_DEV_MODE ?? "").toLowerCase() === "true";

// ── DEV_MODE mock session ───────────────────────────
// When DEV_MODE is enabled, all Cognito functions return mocks.
// The backend (also in DEV_MODE) bypasses JWT validation and uses
// the seeded dev user — so real API calls still work end-to-end.
const _DEV_LS_KEY = "foreman_dev_session";
function _devLoad() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(_DEV_LS_KEY) : null;
    if (raw) return JSON.parse(raw) as { signedIn: boolean; email: string; firstName: string };
  } catch {}
  return { signedIn: false, email: "dev@askforeman.co.uk", firstName: "Dev" };
}
function _devSave() {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(_DEV_LS_KEY, JSON.stringify({ signedIn: _devSignedIn, email: _devEmail, firstName: _devFirstName }));
    }
  } catch {}
}
const _devInit = _devLoad();
let _devSignedIn = _devInit.signedIn;
let _devEmail = _devInit.email;
let _devFirstName = _devInit.firstName;

function _devMockSession(): CognitoUserSession {
  return {
    getIdToken: () => ({
      getJwtToken: () => "dev-local-token",
      payload: {
        sub: "dev-local-user",
        email: _devEmail,
        given_name: _devFirstName,
        "cognito:username": _devEmail,
      },
    }),
    getAccessToken: () => ({ getJwtToken: () => "dev-local-token" }),
    getRefreshToken: () => ({ getToken: () => "dev-local-refresh" }),
    isValid: () => true,
  } as unknown as CognitoUserSession;
}

export { DEV_MODE };

if (!UI_ONLY_MODE && !DEV_MODE && (!POOL_ID || !CLIENT_ID)) {
  console.warn(
    "[auth] Cognito env vars not set - authentication will not work.\n" +
      "Create web-ui/.env with VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_APP_CLIENT_ID",
  );
}

type CognitoRuntime = typeof import("amazon-cognito-identity-js");

let cognitoRuntimePromise: Promise<CognitoRuntime> | null = null;
let userPool: any | null = null;

function polyfillBrowserGlobals() {
  const g = globalThis as unknown as Record<string, unknown>;
  if (!("global" in g)) g.global = globalThis;
  if (!("process" in g)) g.process = { env: {} };
}

async function loadCognitoRuntime(): Promise<CognitoRuntime> {
  if (UI_ONLY_MODE || DEV_MODE) {
    throw new Error("UI_ONLY_MODE or DEV_MODE");
  }
  if (!cognitoRuntimePromise) {
    polyfillBrowserGlobals();
    cognitoRuntimePromise = import("amazon-cognito-identity-js");
  }
  return cognitoRuntimePromise;
}

async function getUserPool(): Promise<any> {
  if (userPool) return userPool;
  const runtime = await loadCognitoRuntime();
  userPool = new runtime.CognitoUserPool({
    UserPoolId: POOL_ID,
    ClientId: CLIENT_ID,
  });
  return userPool;
}

export function signIn(
  email: string,
  password: string,
): Promise<CognitoUserSession> {
  if (DEV_MODE) {
    _devSignedIn = true;
    _devEmail = email;
    _devFirstName = email.split("@")[0] || "Dev";
    _devSave();
    return Promise.resolve(_devMockSession());
  }
  return (async () => {
    const runtime = await loadCognitoRuntime();
    const pool = await getUserPool();
    const user = new runtime.CognitoUser({ Username: email, Pool: pool });
    const auth = new runtime.AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise<CognitoUserSession>((resolve, reject) => {
      user.authenticateUser(auth, {
        onSuccess: (session: CognitoUserSession) => resolve(session),
        onFailure: (err: unknown) => reject(err),
        newPasswordRequired: () => reject(new Error("NEW_PASSWORD_REQUIRED")),
      });
    });
  })();
}

export function signUp(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  phone: string,
): Promise<void> {
  if (DEV_MODE) {
    _devEmail = email;
    _devFirstName = firstName;
    return Promise.resolve();
  }
  return (async () => {
    const runtime = await loadCognitoRuntime();
    const pool = await getUserPool();

    const attributes = [
      new runtime.CognitoUserAttribute({ Name: "email", Value: email }),
      new runtime.CognitoUserAttribute({
        Name: "name",
        Value: `${firstName} ${lastName}`,
      }),
      new runtime.CognitoUserAttribute({
        Name: "given_name",
        Value: firstName,
      }),
      new runtime.CognitoUserAttribute({
        Name: "family_name",
        Value: lastName,
      }),
      new runtime.CognitoUserAttribute({ Name: "phone_number", Value: phone }),
    ];

    return new Promise<void>((resolve, reject) => {
      pool.signUp(email, password, attributes, [], (err: unknown) => {
        if (err) return reject(err);
        resolve();
      });
    });
  })();
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  if (DEV_MODE) return Promise.resolve();
  return (async () => {
    const runtime = await loadCognitoRuntime();
    const pool = await getUserPool();
    const user = new runtime.CognitoUser({ Username: email, Pool: pool });
    return new Promise<void>((resolve, reject) => {
      user.confirmRegistration(code, true, (err: unknown) => {
        if (err) return reject(err);
        resolve();
      });
    });
  })();
}

export function getCurrentSession(): Promise<CognitoUserSession | null> {
  if (DEV_MODE) return Promise.resolve(_devSignedIn ? _devMockSession() : null);
  return (async () => {
    try {
      const pool = await getUserPool();
      const user = pool.getCurrentUser();
      if (!user) return null;

      return new Promise<CognitoUserSession | null>((resolve) => {
        user.getSession(
          (err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session || !session.isValid()) return resolve(null);
            resolve(session);
          },
        );
      });
    } catch {
      return null;
    }
  })();
}

export async function getIdToken(): Promise<string | null> {
  if (DEV_MODE) return _devSignedIn ? "dev-local-token" : null;
  const session = await getCurrentSession();
  return session?.getIdToken().getJwtToken() ?? null;
}

export function signOut(): void {
  if (DEV_MODE) {
    _devSignedIn = false;
    _devSave();
    return;
  }
  const user = userPool?.getCurrentUser?.();
  if (user) user.signOut();
}

export function updateUserAttributes(
  attrs: Record<string, string>,
): Promise<void> {
  if (DEV_MODE) return Promise.resolve();
  return (async () => {
    const runtime = await loadCognitoRuntime();
    const pool = await getUserPool();
    const user = pool.getCurrentUser();
    if (!user) throw new Error("Not signed in");

    return new Promise<void>((resolve, reject) => {
      user.getSession((err: Error | null) => {
        if (err) return reject(err);
        const attrList = Object.entries(attrs).map(
          ([Name, Value]) => new runtime.CognitoUserAttribute({ Name, Value }),
        );
        user.updateAttributes(attrList, (err2: unknown) => {
          if (err2) return reject(err2);
          resolve();
        });
      });
    });
  })();
}

export function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  if (DEV_MODE) return Promise.resolve();
  return (async () => {
    const pool = await getUserPool();
    const user = pool.getCurrentUser();
    if (!user) throw new Error("Not signed in");

    return new Promise<void>((resolve, reject) => {
      user.getSession((err: Error | null) => {
        if (err) return reject(err);
        user.changePassword(oldPassword, newPassword, (err2: unknown) => {
          if (err2) return reject(err2);
          resolve();
        });
      });
    });
  })();
}

export function getCurrentUserEmail(): string | null {
  const user = userPool?.getCurrentUser?.();
  return user?.getUsername?.() ?? null;
}
